import { Button, Card, CardBody } from '@heroui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import { ReactNode } from 'react';

import { FreeToolsInactiveGateProvider, useFreeToolsInactiveGate } from '../../contexts/FreeToolsInactiveGateContext';
import { FreeToolSurface, getFreeToolSurface, isAvailableInKreatliPlatform } from '../../data/free-tool-surface';
import { useFreeToolsEntitlementGate } from '../../hooks/useFreeToolsEntitlementGate';
import { canExportCreatorTools } from '../../utils/canExportCreatorTools';
import { useSession } from '../../hooks/useSession';

/**
 * Soft strip for exploration mode on browser marketing (download) tools.
 */
function BrowserExploreUnlockStrip({
  continueCta,
}: {
  continueCta: { href: string; label: string } | null;
}) {
  const { openUnlockModal, openInactivePlanModal } = useFreeToolsInactiveGate();

  return (
    <Card className="dark:bg-primary-950/30 border border-primary-200 bg-primary-50/60 shadow-sm dark:border-primary-800">
      <CardBody className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground-600">
          Preview is free. Downloads need a one-time <span className="font-medium text-foreground">$4.99 lifetime unlock</span>{' '}
          (or an active Kreatli plan).
        </p>
        <div className="flex shrink-0 flex-row flex-wrap gap-2">
          <Button size="sm" className="bg-foreground text-content1" onPress={() => openUnlockModal({ entry: 'strip' })}>
            Unlock for $4.99
          </Button>
          <Button size="sm" variant="bordered" onPress={() => openInactivePlanModal({ force: true })}>
            Start free trial
          </Button>
          {continueCta && (
            <Button as={NextLink} href={continueCta.href} size="sm" variant="bordered">
              {continueCta.label}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Soft strip for exploration mode on platform preview tools (workspace-oriented).
 */
function ExploreModeStrip({
  continueCta,
  upgradeCta,
}: {
  continueCta: { href: string; label: string } | null;
  upgradeCta: { href: string; label: string } | null;
}) {
  const { openInactivePlanModal } = useFreeToolsInactiveGate();

  return (
    <Card className="dark:bg-primary-950/30 border border-primary-200 bg-primary-50/60 shadow-sm dark:border-primary-800">
      <CardBody className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground-600">
          <span className="font-medium text-foreground">Exploration Mode</span> — 1 project · 2 files · 3 collaborators.{' '}
          <span className="hidden sm:inline">Start a free trial to unlock unlimited access.</span>
        </p>
        <div className="flex shrink-0 flex-row gap-2">
          <Button
            size="sm"
            className="bg-foreground text-content1"
            onPress={() => openInactivePlanModal({ force: true })}
          >
            {upgradeCta?.label ?? 'Start free trial'}
          </Button>
          {continueCta && (
            <Button as={NextLink} href={continueCta.href} size="sm" variant="bordered">
              {continueCta.label}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

const DEFAULT_PLATFORM_LOCKED_DESCRIPTION =
  "You're signed in without an active trial or plan. Start a trial or choose a plan to use this tool here, or go to your workspace.";

const DEFAULT_BROWSER_LOCKED_DESCRIPTION =
  "Your trial or plan isn't active. Start a trial or choose a plan to use this tool on this page.";

function getLockedBannerCopy(
  pathname: string,
  lockedTitle?: string,
  lockedDescription?: string,
): { title?: string; description: string } {
  if (isAvailableInKreatliPlatform(pathname)) {
    return {
      title: lockedTitle,
      description: lockedDescription ?? DEFAULT_PLATFORM_LOCKED_DESCRIPTION,
    };
  }

  // Browser-only tools: drop misleading "inside Kreatli" headlines from page props.
  const title = lockedTitle && !/available inside Kreatli/i.test(lockedTitle) ? lockedTitle : undefined;
  const description =
    lockedDescription && !/in Kreatli/i.test(lockedDescription)
      ? lockedDescription
      : DEFAULT_BROWSER_LOCKED_DESCRIPTION;

  return { title, description };
}

/**
 * Soft non-blocking strip for browser marketing tools (expired trial / locked).
 */
function BrowserToolSoftBanner() {
  const { openUnlockModal, openInactivePlanModal } = useFreeToolsInactiveGate();

  return (
    <Card className="border border-foreground-200 bg-content1/70 shadow-sm">
      <CardBody className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-foreground-600">
          You are currently in view-only mode. To export, purchase a one-time lifetime unlock for $4.99 or start a
          Kreatli plan.
        </p>
        <div className="flex shrink-0 flex-row flex-wrap gap-2">
          <Button size="sm" className="bg-foreground text-content1" onPress={() => openUnlockModal({ entry: 'strip' })}>
            Unlock for $4.99
          </Button>
          <Button size="sm" variant="bordered" onPress={() => openInactivePlanModal({ force: true })}>
            Start free trial
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

/**
 * Renders the upgrade CTA inside the hard locked banner (platform previews).
 */
function LockedBannerUpgradeCta({ label, variant = 'solid' }: { label: string; variant?: 'solid' | 'bordered' }) {
  const { openInactivePlanModal } = useFreeToolsInactiveGate();

  return (
    <Button
      size="sm"
      variant={variant === 'bordered' ? 'bordered' : undefined}
      className={variant === 'solid' ? 'bg-foreground text-content1' : undefined}
      onPress={() => openInactivePlanModal({ force: true })}
    >
      {label}
    </Button>
  );
}

export function FreeToolsEntitlementSection(props: {
  children: ReactNode;
  lockedTitle?: string;
  lockedDescription?: string;
  /** Where to render the explore/locked strip relative to the tool. Default: 'above'. */
  stripPosition?: 'above' | 'below';
}) {
  const { children, lockedTitle, lockedDescription, stripPosition = 'above' } = props;
  const gate = useFreeToolsEntitlementGate();
  const { user } = useSession();
  const { pathname } = useRouter();
  const surface = getFreeToolSurface(pathname);
  const alreadyEntitled = canExportCreatorTools(user);

  // Exploration mode: soft strip. Browser download tools lead with $4.99 unlock.
  if (gate.isExploreMode) {
    const strip =
      surface === 'browser_marketing' && !alreadyEntitled ? (
        <BrowserExploreUnlockStrip continueCta={gate.continueCta} />
      ) : (
        <ExploreModeStrip continueCta={gate.continueCta} upgradeCta={gate.upgradeCta} />
      );
    return (
      <FreeToolsInactiveGateProvider isInactiveLocked={false} surface={surface}>
        <div className="space-y-3">
          {stripPosition === 'above' && strip}
          {children}
          {stripPosition === 'below' && strip}
        </div>
      </FreeToolsInactiveGateProvider>
    );
  }

  if (gate.isLocked) {
    // Browser marketing tools: soft non-blocking strip — tool is fully interactive.
    if (surface === 'browser_marketing') {
      return (
        <FreeToolsInactiveGateProvider isInactiveLocked surface={surface}>
          <div className="space-y-3">
            {!alreadyEntitled ? <BrowserToolSoftBanner /> : null}
            {children}
          </div>
        </FreeToolsInactiveGateProvider>
      );
    }

    // Platform previews + others: hard locked banner (expired trial).
    const lockedCopy = getLockedBannerCopy(pathname, lockedTitle, lockedDescription);
    return (
      <FreeToolsInactiveGateProvider isInactiveLocked surface={surface}>
        <div className="space-y-3">
          <Card className="border border-foreground-200 bg-content1/70 shadow-sm">
            <CardBody className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-foreground-600">
                {lockedCopy.title ? <p className="mb-1 font-medium text-foreground">{lockedCopy.title}</p> : null}
                <p>{lockedCopy.description}</p>
              </div>
              <div className="flex flex-col gap-2 sm:shrink-0 sm:flex-row">
                {gate.upgradeCta ? <LockedBannerUpgradeCta label={gate.upgradeCta.label} /> : null}
                {gate.continueCta ? (
                  <Button as={NextLink} href={gate.continueCta.href} size="sm" variant="bordered">
                    {gate.continueCta.label}
                  </Button>
                ) : null}
              </div>
            </CardBody>
          </Card>
          {children}
        </div>
      </FreeToolsInactiveGateProvider>
    );
  }

  // Signed-out / active / entitled: still wrap so download tools can open the unlock modal.
  return (
    <FreeToolsInactiveGateProvider isInactiveLocked={false} surface={surface}>
      <div className="space-y-3">
        {gate.continueCta && (
          <Card className="border border-foreground-200 bg-content1/70 shadow-sm">
            <CardBody className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="text-sm text-foreground-600">
                {surface === 'browser_marketing'
                  ? 'Need review & feedback on your work? Upload it to Kreatli for frame-accurate comments, approvals, and team collaboration.'
                  : "You're signed in. Keep using this tool here, or continue in your Kreatli workspace."}
              </div>
              <div className="flex flex-col gap-2 sm:shrink-0 sm:flex-row">
                <Button as={NextLink} href={gate.continueCta.href} size="sm" className="bg-foreground text-content1">
                  {gate.continueCta.label}
                </Button>
                {gate.upgradeCta ? <LockedBannerUpgradeCta label={gate.upgradeCta.label} variant="bordered" /> : null}
              </div>
            </CardBody>
          </Card>
        )}
        {children}
      </div>
    </FreeToolsInactiveGateProvider>
  );
}
